import { Box, Stack, TableCell, Typography } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { GRAY_700, GRAY_800, GRAY_900 } from 'constants/styling/theme'
import { MUITable, MUITableColumn } from 'components/common/MUITable/MUITable.component'
import { PendingInvitation, WorkspaceMember, WorkspaceMemberProfile, WorkspaceRole, WorkspaceSeatType } from 'models/user/userWorkspaceDTOs'

import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUISwitch } from 'components/common/MUISwitch'
import { MUITooltip } from 'components/common/MUITooltip'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { NameAvatar } from 'components/common/Avatars'
import { ResendInvitationButton } from 'components/pages/Client/WorkspacesProfile/ResendInvitationButton'
import { SeatTypeDropdown } from '../SeatTypeDropdown'
import { useMembersContentContext } from './MembersContent.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useWorkspacesProfileContext } from 'components/pages/Client/WorkspacesProfile/_main/WorkspacesProfile.context'

export type MemberRow = { type: 'member'; data: WorkspaceMemberProfile }
export type PendingRow = { type: 'pending'; data: PendingInvitation }
export type MemberTableRow = MemberRow | PendingRow

export const getRowSortName = (row: MemberTableRow): string => (row.type === 'member' ? row.data.name.toLowerCase() : row.data.email.toLowerCase())

export const MembersTable: FC = () => {
  const { t, i18n } = useTranslation(['profile'])
  const { currentUserWorkspace, baseUserData } = useUserData()
  const { workspaceId, pendingInvitations } = useWorkspacesProfileContext()
  const {
    sortedRows,
    isViewedWorkspaceAdmin,
    showCanOrderColumn,
    allowedSeatTypes,
    isPaidPlan,
    isAccessCellDisabled,
    isActionCellDisabled,
    isGrantAdminDisabled,
    onGrantOrRevokeAdmin,
    onChangeSeatType,
    onChangeMemberCanOrder,
    onRevokeInvitation,
    isRevokeInvitationPending,
    setRemoveMember,
  } = useMembersContentContext()

  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

  const tableData = useMemo(() =>
    sortedRows.map((row) => ({
      id: row.type === 'pending' ? row.data.id : row.data.membershipId,
      data: row,
    })), [sortedRows])

  const hasPendingInvitations = useMemo(() => pendingInvitations && pendingInvitations.length > 0, [pendingInvitations])

  const columns = useMemo(() => {
    const cols: MUITableColumn<MemberTableRow>[] = [
      {
        name: 'NAME',
        translation: t('profile:members_content.column_name'),
        size: '40rem',
        mobileSize: '30rem',
        isSortable: false,
        renderCell: ({ id, data: row }) => {
          const isPending = row.type === 'pending'
          return (
            <TableCell key={`${id}_NAME`}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <NameAvatar name={isPending ? row.data.email : row.data.name} size="4.8rem" textVariant="text-xs" elevation="sm" />
                <Stack sx={{ minWidth: 0, flex: 1 }}>
                  {isPending ? (
                    <>
                      <Typography variant="text-sm" color={GRAY_700} height="2rem">
                        {row.data.email}
                      </Typography>
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <Typography variant="text-xs" color={GRAY_700}>
                          {t('profile:members_content.pending')}
                        </Typography>
                        {isViewedWorkspaceAdmin && (
                          <>
                            <Box sx={{ width: '0.4rem', height: '0.4rem', aspectRatio: '1/1', borderRadius: '50%', backgroundColor: '#DCDDDE' }} />
                            <Box sx={{ maxWidth: 'fit-content' }}>
                              <ResendInvitationButton type="secondaryNoBorder" workspaceId={workspaceId} invitation={row.data} size="xs" workspaceDataOfUser={{ userId: baseUserData?.id ?? '', role: currentUserWorkspace?.role } as WorkspaceMember} />
                            </Box>
                          </>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography variant="text-sm" fontWeight={550} color={GRAY_900} height="2rem">
                        {row.data.name}
                      </Typography>
                      <Typography variant="text-xs" fontWeight={350} color={GRAY_700} noWrap>
                        {row.data.email}
                      </Typography>
                    </>
                  )}
                </Stack>
                {!isPending && row.data.isOwner && <MUIBadge label={t('profile:members_content.owner')} color="blue" size="sm" />}
                {!isPending && row.data.role === WorkspaceRole.ADMIN && <MUIBadge label={t('profile:members_content.admin')} color="gray" size="sm" />}
              </Stack>
            </TableCell>
          )
        },
      },
      {
        name: 'SEAT_TYPE',
        translation: t('profile:members_content.column_seat_type'),
        size: '30rem',
        mobileSize: '25rem',
        isSortable: false,
        renderCell: ({ id, data: row }) => {
          const isPending = row.type === 'pending'
          return (
            <TableCell key={`${id}_SEAT_TYPE`} sx={{ padding: '1.6rem 1.5rem !important' }}>
              {isPending ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ borderRadius: '0.8rem', padding: '0 1.2rem', height: '4.2rem' }}>
                  <Typography variant="text-sm" fontWeight={350} color={GRAY_700}>
                    -
                  </Typography>
                </Stack>
              ) : (
                <SeatTypeDropdown member={row.data} allowedSeatTypes={allowedSeatTypes} isPaidPlan={isPaidPlan} disabled={isAccessCellDisabled(row.data)} openDropdownId={openDropdownId} onOpen={setOpenDropdownId} onClose={() => setOpenDropdownId(null)} onChangeSeatType={onChangeSeatType} />
              )}
            </TableCell>
          )
        },
      },
      {
        name: 'PROPERTIES_ACCESS',
        translation: t('profile:members_content.column_properties_access'),
        size: hasPendingInvitations ? '10rem' : '12rem',
        mobileSize: '10rem',
        isSortable: false,
        renderCell: ({ id, data: row }) => {
          const isPending = row.type === 'pending'
          return (
            <TableCell key={`${id}_PROPERTIES_ACCESS`}>
              <Typography variant="text-sm" fontWeight={350} color={isPending ? GRAY_700 : GRAY_900}>
                {isPending ? '-' : row.data.seatType === WorkspaceSeatType.FULL ? t('profile:members_content.all') : row.data.numberOfRealEstateProperties}
              </Typography>
            </TableCell>
          )
        },
      },
    ]

    if (showCanOrderColumn) {
      cols.push({
        name: 'CAN_ORDER',
        translation: t('profile:members_content.column_can_order'),
        size: '12rem',
        mobileSize: '10rem',
        isSortable: false,
        renderCell: ({ id, data: row }) => <TableCell key={`${id}_CAN_ORDER`}>{row.type === 'member' && <MUISwitch size="medium" checked={!!row.data.canOrder} onChange={() => onChangeMemberCanOrder(row.data, !row.data.canOrder)} disabled={!isViewedWorkspaceAdmin} />}</TableCell>,
      })
    }

    cols.push({
      name: 'ACTIONS',
      translation: '',
      size: hasPendingInvitations ? 'auto' : '10rem',
      mobileSize: 'auto',
      isSortable: false,
      renderCell: ({ id, data: row }) => {
        const isPending = row.type === 'pending'
        return (
          <TableCell key={`${id}_ACTIONS`} align="center">
            {!isPending && !isActionCellDisabled(row.data) && (
              <MUIDropdown
                open={openDropdownId === `actions-${row.data.membershipId}`}
                onClose={() => setOpenDropdownId(null)}
                button={(isOpen) => (
                  <MUIButton
                    isIconButton
                    size="sm"
                    type="secondaryNoBorder"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (isOpen) return setOpenDropdownId(null)
                      setOpenDropdownId(`actions-${row.data.membershipId}`)
                    }}
                  >
                    <MoreHorizIcon fontSize="large" sx={{ color: GRAY_800 }} />
                  </MUIButton>
                )}
              >
                {/* In a paid workspace only a Full-seat member can be promoted to admin (ISSUE-2613);
                    the option stays visible but disabled, with a tooltip explaining why. */}
                <MUITooltip content={isGrantAdminDisabled(row.data) ? t('profile:members_content.grant_admin_requires_full_seat_tooltip') : undefined} placement="left">
                  <MUIDropdownItem disabled={isGrantAdminDisabled(row.data)} onClick={() => onGrantOrRevokeAdmin(row.data)}>
                    <Typography variant="text-md" color={GRAY_900}>
                      {row.data.role === WorkspaceRole.ADMIN ? t('profile:members_content.revoke_admin_access') : t('profile:members_content.grant_admin_access')}
                    </Typography>
                  </MUIDropdownItem>
                </MUITooltip>
                <MUIDropdownItem onClick={() => setRemoveMember(row.data)}>
                  <Typography variant="text-md" color={GRAY_900}>
                    {t('profile:members_content.remove_and_transfer')}
                  </Typography>
                </MUIDropdownItem>
              </MUIDropdown>
            )}

            {isPending && isViewedWorkspaceAdmin && (
              <MUIButton size="sm" type="secondaryNoBorder" sx={{ flex: 1 }} isLoading={isRevokeInvitationPending(row.data.id)} onClick={() => onRevokeInvitation(row.data.id)}>
                <Typography variant="text-md" color={GRAY_900} noWrap>
                  {t('profile:members_content.remove_invitation')}
                </Typography>
              </MUIButton>
            )}
          </TableCell>
        )
      },
    })

    return cols
  }, [showCanOrderColumn, allowedSeatTypes, isPaidPlan, isViewedWorkspaceAdmin, openDropdownId, hasPendingInvitations, isAccessCellDisabled, isActionCellDisabled, isGrantAdminDisabled, onGrantOrRevokeAdmin, onChangeSeatType, onChangeMemberCanOrder, setRemoveMember, onRevokeInvitation, isRevokeInvitationPending, baseUserData?.id, currentUserWorkspace?.role, workspaceId, t])

  return <MUITable columns={columns} data={tableData} />
}
